
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export interface Link {
  id: string;
  link_type: 'facebook' | 'instagram' | 'youtube' | 'website' | 'spotify' | 'obituary' | 'news_article' | 'blog' | 'charity' | 'memorial_fund' | 'linkedin' | 'twitter' | 'other';
  link_url: string;
  link_label?: string;
  order_index: number;
}

export const useLinks = (memorialId: string) => {
  const [links, setLinks] = useState<Link[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLinks = useCallback(async () => {
    if (!memorialId) {
      console.error('No memorial ID provided');
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('links')
        .select('*')
        .eq('memorial_id', memorialId)
        .order('order_index');

      if (error) throw error;

      const typedLinks = data?.map(link => ({
        id: link.id,
        link_type: link.link_type as Link['link_type'],
        link_url: link.link_url,
        link_label: link.link_label,
        order_index: link.order_index
      })) || [];

      setLinks(typedLinks);
    } catch (error: unknown) {
      console.error('Error fetching links:', error);
      toast({
        title: "Error",
        description: "Failed to load external links",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [memorialId]);

  const addLink = async (linkType: Link['link_type'], url: string, label?: string) => {
    if (!memorialId) {
      toast({
        title: "Error",
        description: "No memorial ID provided",
        variant: "destructive",
      });
      return false;
    }

    try {
      const maxOrderIndex = Math.max(...links.map(link => link.order_index), -1);
      const { data, error } = await supabase
        .from('links')
        .insert({
          memorial_id: memorialId,
          link_type: linkType,
          link_url: url,
          link_label: label || null,
          order_index: maxOrderIndex + 1
        })
        .select()
        .single();

      if (error) throw error;
      
      const newLink: Link = {
        id: data.id,
        link_type: data.link_type as Link['link_type'],
        link_url: data.link_url,
        link_label: data.link_label,
        order_index: data.order_index
      };
      
      setLinks([...links, newLink]);
      toast({
        title: "Success",
        description: "Link added successfully",
      });
      return true;
    } catch (error: unknown) {
      console.error('Error adding link:', error);
      const errorMessage = error instanceof Error ? error.message :
        (typeof error === 'object' && error !== null && 'message' in error) ? String((error as { message: unknown }).message) : 'Unknown error';
      toast({
        title: "Error",
        description: `Failed to add link: ${errorMessage}`,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateLink = async (linkId: string, updates: Partial<Omit<Link, 'id'>>) => {
    if (!memorialId) {
      toast({
        title: "Error",
        description: "No memorial ID provided",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('links')
        .update(updates)
        .eq('id', linkId);

      if (error) throw error;
      setLinks(links.map(link => link.id === linkId ? { ...link, ...updates } : link));
      toast({
        title: "Success",
        description: "Link updated successfully",
      });
      return true;
    } catch (error: unknown) {
      console.error('Error updating link:', error);
      toast({
        title: "Error",
        description: "Failed to update link",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteLink = async (linkId: string) => {
    if (!memorialId) {
      toast({
        title: "Error",
        description: "No memorial ID provided",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('links')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      setLinks(links.filter(link => link.id !== linkId));
      toast({
        title: "Success",
        description: "Link deleted successfully",
      });
      return true;
    } catch (error: unknown) {
      console.error('Error deleting link:', error);
      toast({
        title: "Error",
        description: "Failed to delete link",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  return {
    links,
    isLoading,
    addLink,
    updateLink,
    deleteLink,
  };
};
